import API_BASE from '../config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useRole } from '../hooks/useRole';
import pricing from '../data/pricing';

const SECTORS = [
  'Technology, Information and Media','Financial Services','Hospitals and Health Care',
  'Manufacturing','Professional Services','Consumer Services','Retail','Education',
  'Real Estate and Equipment Rental','Construction','Transportation, Logistics, Supply Chain and Storage',
  'Energy, Mining and Utilities','Government Administration','Non-profit Organizations',
  'Entertainment Providers','Accommodation and Food Services','Administrative and Support Services',
  'Farming, Ranching and Forestry','Civic and Social Organizations','International Affairs',
  'Public Safety','Holding Companies','Religious Institutions','Other'
];

const TRACK_OPTIONS = Object.entries(pricing).map(([key, val]) => ({ key, label: val.label }));
function tierOptions(track) {
  if (!track || !pricing[track]) return [];
  return Object.entries(pricing[track].tiers).map(([key, val]) => ({ key, ...val }));
}

const inputStyle = { width: '100%', backgroundColor: '#fff', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.6rem 0.8rem', color: '#1A1A1A', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none' };
const labelStyle = { display: 'block', fontSize: '0.8rem', color: '#4A5568', marginBottom: '0.35rem', fontWeight: '500' };
const EMPTY_FORM = { name: '', sector: '', website: '', contact_name: '', contact_email: '', service_track: '', service_tier: '', notes: '', account_id: '', cta_url: '', landing_page_url: '', attachment_url: '', attachment_name: '' };

function ClientInitial({ name, website }) {
  const initials = name ? name.split(" ").map(w => w[0]).slice(0, 2).join("") : "?";
  const domain = website ? website.replace(/^https?:\/\//, "").replace(/\/.*$/, "") : null;
  const faviconUrl = domain ? "https://www.google.com/s2/favicons?domain=" + domain + "&sz=64" : null;
  if (faviconUrl) return <div style={{ width: "40px", height: "50px", borderRadius: "8px", background: "#F5E6E6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}><img src={faviconUrl} alt={name} style={{ width: "28px", height: "50px", objectFit: "contain" }} onError={e => e.target.style.display="none"} /></div>;
  return <div style={{ width: "40px", height: "50px", borderRadius: "8px", background: "#F5E6E6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "500", color: "#8B1A1A", flexShrink: 0 }}>{initials}</div>;
}


function ClientsList() {
  const { isSuperAdmin: _isSuperAdmin, isTeamMember: _isTeamMember, isClientOrMember: _isClientOrMember, clientId: _gateClientId, loading: _roleLoading } = useRole();
  const _gateNavigate = useNavigate();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filterAccount, setFilterAccount] = useState('all');
  const [filterTrack, setFilterTrack] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const { getToken } = useAuth();
  // Auth gate: only super_admin and team members may view this list.
  React.useEffect(() => {
    if (_roleLoading) return;
    if (!_isSuperAdmin && !_isTeamMember) {
      if (_isClientOrMember && _gateClientId) _gateNavigate('/clients/' + _gateClientId, { replace: true });
      else _gateNavigate('/', { replace: true });
    }
  }, [_roleLoading, _isSuperAdmin, _isTeamMember, _isClientOrMember, _gateClientId, _gateNavigate]);

  useEffect(() => { fetchClients(); fetchAccounts(); }, []);

  async function fetchAccounts() {
    try {
      const token = await getToken();
      const r = await fetch(API_BASE + '/accounts', { headers: { 'Authorization': 'Bearer ' + token } });
      const d = await r.json();
      setAccounts(Array.isArray(d) ? d : []);
    } catch (e) {}
  }

  async function fetchClients() {
    try {
      const res = await fetch(`${API_BASE}/clients`);
      const data = await res.json();
      setClients(data);
    } catch (err) { setError('Could not load clients.'); }
  }

  function openNewForm() {
    setEditingClient(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  }

  function openEditForm(e, client) {
    e.stopPropagation();
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      sector: client.sector || '',
      website: client.website || '',
      contact_name: client.contact_name || '',
      contact_email: client.contact_email || '',
      service_track: client.service_track || '',
      service_tier: client.service_tier || '',
      notes: client.notes || '',
      cta_url: client.cta_url || '',
      landing_page_url: client.landing_page_url || '',
      attachment_url: client.attachment_url || '',
      attachment_name: client.attachment_name || '',
    });
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingClient(null);
    setFormData(EMPTY_FORM);
  }

  function setField(field, value) {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'service_track') next.service_tier = '';
      return next;
    });
  }

  async function handleSave() {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      const token = await getToken();
      if (editingClient) {
        const res = await fetch(`${API_BASE}/clients/${editingClient.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
          body: JSON.stringify(formData)
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.error || `Save failed (${res.status})`);
        }
        const updated = await res.json();
        setClients(clients.map(c => c.id === editingClient.id ? updated : c));
      } else {
        const res = await fetch(`${API_BASE}/clients`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
          body: JSON.stringify({ ...formData, status: 'active' })
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.error || `Create failed (${res.status})`);
        }
        const newClient = await res.json();
        setClients([newClient, ...clients]);
      }
      closeForm();
    } catch (err) { setError(err.message || 'Could not save client.'); }
    setSaving(false);
  }

  async function handleDelete(e, client) {
    e.stopPropagation();
    if (!window.confirm(`Delete ${client.name}? This cannot be undone.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/clients/${client.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (!res.ok) {
        const body = await res.text();
        setError(`Delete failed (${res.status}): ${body}`);
        return;
      }
      setClients(clients.filter(c => c.id !== client.id));
    } catch (err) { setError('Could not delete client: ' + err.message); }
  }

  async function handleSetStatus(e, client, newStatus) {
    e.stopPropagation();
    const verb = newStatus === 'active' ? 'reactivate' : 'deactivate';
    if (!window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} ${client.name}?`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/clients/${client.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ status: newStatus })
      });
      if (!res.ok) {
        const body = await res.text();
        setError(`Status update failed (${res.status}): ${body}`);
        return;
      }
      const updated = await res.json();
      setClients(clients.map(c => c.id === client.id ? { ...c, status: updated.status } : c));
    } catch (err) { setError('Could not update status: ' + err.message); }
  }

  const currentTiers = tierOptions(formData.service_track);
  const selectedTier = formData.service_track && formData.service_tier
    ? pricing[formData.service_track]?.tiers[formData.service_tier]
    : null;

  const visibleClients = clients.filter(c => {
    if (filterAccount !== 'all' && String(c.account_id) !== String(filterAccount)) return false;
    if (filterTrack !== 'all' && c.account_plan_track !== filterTrack) return false;
    if (!showInactive && c.status && c.status !== 'active') return false;
    return true;
  });

  if (_roleLoading) return null;
  if (!_isSuperAdmin && !_isTeamMember) return null;
  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.25rem', color: '#1A1A1A' }}>Clients</h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>{clients.length} active {clients.length === 1 ? 'client' : 'clients'}</p>
          </div>
          <button onClick={openNewForm} style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '500' }}>+ New Client</button>
        </div>

        {error && <p style={{ color: '#C53030', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</p>}

        {showForm && (
          <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: '500', marginTop: 0, marginBottom: '1.5rem', color: '#1A1A1A' }}>{editingClient ? `Edit — ${editingClient.name}` : 'New client'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Client name *</label>
                <input type="text" value={formData.name} onChange={e => setField('name', e.target.value)} placeholder="e.g. Fadac Resources" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Website</label>
                <input type="text" value={formData.website} onChange={e => setField('website', e.target.value)} placeholder="e.g. fadacresources.com" style={inputStyle} />
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Sector</label>
              <select value={formData.sector} onChange={e => setField('sector', e.target.value)} style={inputStyle}>
                <option value="">Select sector</option>
                {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Contact name</label>
                <input type="text" value={formData.contact_name} onChange={e => setField('contact_name', e.target.value)} placeholder="e.g. John Smith" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Contact email</label>
                <input type="email" value={formData.contact_email} onChange={e => setField('contact_email', e.target.value)} placeholder="e.g. john@company.com" style={inputStyle} />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Account *</label>
              <select value={formData.account_id || ''} onChange={e => setField('account_id', e.target.value)} style={inputStyle}>
                <option value="">Select account</option>
                {accounts.filter(a => a.status === 'active').map(a => (
                  <option key={a.id} value={a.id}>{a.name} — {a.plan_track}{a.plan_tier ? ' / ' + a.plan_tier : ''}</option>
                ))}
              </select>
              <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: '#888' }}>Workspace will be billed under this account.</p>
            </div>

            {/* Service track toggle */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Service track</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {TRACK_OPTIONS.map(t => (
                  <button key={t.key} type="button" onClick={() => setField('service_track', t.key)}
                    style={{ padding: '0.55rem 0.75rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '500', cursor: 'pointer', textAlign: 'center',
                      border: formData.service_track === t.key ? '2px solid #8B1A1A' : '1px solid #D4CFC9',
                      backgroundColor: formData.service_track === t.key ? '#FFF5F5' : '#fff',
                      color: formData.service_track === t.key ? '#8B1A1A' : '#4A5568' }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Service tier dropdown */}
            {formData.service_track && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Service tier</label>
                <select value={formData.service_tier} onChange={e => setField('service_tier', e.target.value)} style={inputStyle}>
                  <option value="">Select tier</option>
                  {currentTiers.map(t => (
                    <option key={t.key} value={t.key}>{t.name} — {t.label}</option>
                  ))}
                </select>
                {selectedTier && (
                  <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: '#888' }}>{selectedTier.label} · billed monthly</p>
                )}
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Notes</label>
              <textarea value={formData.notes} onChange={e => setField('notes', e.target.value)} placeholder="Any additional context about this client" rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.5', fontFamily: 'inherit' }} />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>CTA URL <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span></label>
              <input type="url" value={formData.cta_url} onChange={e => setField('cta_url', e.target.value)} placeholder="https://cal.com/you or booking link" style={inputStyle} />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Default landing page URL</label>
              <div style={{ fontSize: '0.7rem', color: '#777', marginBottom: '0.2rem' }}>Resolves the {'{{landing_page}}'} token (product page, case study)</div>
              <input type="url" value={formData.landing_page_url} onChange={e => setField('landing_page_url', e.target.value)} placeholder="https://yoursite.com/case-study" style={inputStyle} />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Default attachment URL</label>
              <div style={{ fontSize: '0.7rem', color: '#777', marginBottom: '0.2rem' }}>Resolves the {'{{attachment}}'} token (deck, brochure, doc link)</div>
              <input type="url" value={formData.attachment_url} onChange={e => setField('attachment_url', e.target.value)} placeholder="https://yoursite.com/deck.pdf" style={inputStyle} />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Default attachment name</label>
              <div style={{ fontSize: '0.7rem', color: '#777', marginBottom: '0.2rem' }}>Used in the intro phrase, e.g. "Attached our 2026 GTM Report"</div>
              <input type="text" value={formData.attachment_name} onChange={e => setField('attachment_name', e.target.value)} placeholder="2026 GTM Report" style={inputStyle} />
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.35rem' }}>
                The product or page prospects should visit. Added to sales-step CTAs only. Leave blank for no link.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={handleSave} disabled={saving || !formData.name.trim()} style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '500', opacity: saving || !formData.name.trim() ? 0.5 : 1 }}>
                {saving ? 'Saving...' : editingClient ? 'Save changes' : 'Save client'}
              </button>
              <button onClick={closeForm} style={{ backgroundColor: 'transparent', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)}
            style={{ fontSize: '0.8rem', padding: '0.35rem 0.6rem', border: '0.5px solid #E8E4E0', borderRadius: '6px', background: '#FFF', cursor: 'pointer' }}
            title="Filter by account">
            <option value="all">All accounts</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select value={filterTrack} onChange={e => setFilterTrack(e.target.value)}
            style={{ fontSize: '0.8rem', padding: '0.35rem 0.6rem', border: '0.5px solid #E8E4E0', borderRadius: '6px', background: '#FFF', cursor: 'pointer' }}>
            <option value="all">All plans</option>
            <option value="managed_service">Managed</option>
            <option value="saas">SaaS</option>
            <option value="internal">Internal</option>
          </select>
          <label style={{ fontSize: '0.8rem', color: '#666', display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', padding: '0.35rem 0.6rem' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
        </div>

        {clients.length === 0 && !showForm ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#888', backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px' }}>
            <p style={{ fontSize: '1rem', marginBottom: '0.5rem', color: '#4A5568', fontWeight: '500' }}>No clients yet</p>
            <p style={{ fontSize: '0.85rem', margin: 0 }}>Click "New Client" to create your first workspace.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {visibleClients.map(client => (
              <div key={client.id} onClick={() => navigate('/clients/' + client.id)}
                style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '10px', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                  <ClientInitial name={client.name} website={client.website} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: '500', fontSize: '0.95rem', color: '#1A1A1A' }}>{client.name}</p>
                    <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: '#888' }}>{client.sector || 'No sector'}{client.website ? ` · ${client.website}` : ''}</p>
                    {(client.contact_name || client.contact_email) && (
                      <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: '#AAA' }}>
                        {client.contact_name}{client.contact_name && client.contact_email ? ' · ' : ''}{client.contact_email}
                      </p>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0, marginLeft: '1rem' }}>
                  {client.account_name && (
                    <span style={{ fontSize: '0.72rem', backgroundColor: '#EBF8FF', color: '#2B6CB0', padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: '500' }}>
                      {client.account_name}
                    </span>
                  )}
                  {client.service_tier && (
                    <span style={{ fontSize: '0.72rem', backgroundColor: '#FFF5F5', color: '#8B1A1A', padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: '500' }}>
                      {pricing[client.service_track]?.tiers[client.service_tier]?.label || client.service_tier}
                    </span>
                  )}
                  {(() => {
                    const s = client.status || 'active';
                    const colours = s === 'active' ? { bg: '#F0FFF4', fg: '#276749' } : s === 'inactive' ? { bg: '#FFFAF0', fg: '#B7791F' } : { bg: '#FED7D7', fg: '#9B2C2C' };
                    return <span style={{ fontSize: '0.72rem', backgroundColor: colours.bg, color: colours.fg, padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: '500' }}>{s}</span>;
                  })()}
                  <button onClick={e => openEditForm(e, client)} style={{ fontSize: '0.72rem', color: '#1A56A0', background: 'none', border: '1px solid #BFD7F5', borderRadius: '20px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>Edit</button>
                  {(client.status || 'active') === 'active' ? (
                    <button onClick={e => handleSetStatus(e, client, 'inactive')} style={{ fontSize: '0.72rem', color: '#B7791F', background: 'none', border: '1px solid #F6E05E', borderRadius: '20px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>Deactivate</button>
                  ) : (
                    <button onClick={e => handleSetStatus(e, client, 'active')} style={{ fontSize: '0.72rem', color: '#276749', background: 'none', border: '1px solid #9AE6B4', borderRadius: '20px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>Reactivate</button>
                  )}
                  <button onClick={e => handleDelete(e, client)} style={{ fontSize: '0.72rem', color: '#C53030', background: 'none', border: '1px solid #FEB2B2', borderRadius: '20px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientsList;
